import { useQuery } from '@tanstack/react-query';
import { get } from '../client';

/**
 * Wrappers for BI-owned report endpoints. These preserve the response
 * shapes that the Reports page originally consumed from staff APIs while
 * keeping BI ACL and store scoping isolated under `/api/bi/`.
 *
 * - Day Close summary  → GET /api/bi/reports/day-close/
 * - Product Sales      → GET /api/bi/reports/product-sales/
 */

export interface OrderSummaryResponse {
  filters: {
    from: string;
    to: string;
    time_start: string | null;
    time_end: string | null;
    store_id: number | null;
  };
  range: { start_iso: string; end_iso: string };
  totals: {
    orders: number;
    gross_final_total: number;
    completed_revenue: number;
    delivery_fee_total: number;
    small_cart_fee_total: number;
    total_profit: number;
    profit_margin_percentage: number;
    avg_order_value: number;
    avg_completion_time_minutes: number;
  };
  status_counts: Record<
    'created' | 'packer_assigned' | 'packed' | 'billing' | 'picked' | 'completed' | 'cancelled',
    number
  >;
  payment_mix: { cod: number; online: number };
  timeline: { first_order_ts: string | null; last_order_ts: string | null };
  store_breakdown: {
    store_id: number;
    store_name: string;
    orders: number;
    completed_revenue: number;
    total_profit: number;
    profit_margin_percentage: number;
  }[];
  status_histogram: { status: string; count: number }[];
}

export interface ProductSalesRow {
  product_id: number | null;
  product_name: string;
  size: string;
  measurement: string;
  subcategory: string;
  price: number;
  quantity: number;
  amount: number;
  order_count: number;
}

export interface ProductSalesResponse {
  results: ProductSalesRow[];
  pagination?: {
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
  };
  filters?: Record<string, unknown>;
  range?: Record<string, unknown>;
  // The base sales report wraps things — keep loose for forward-compat.
  [k: string]: unknown;
}

interface DayParams {
  /** YYYY-MM-DD (Asia/Kathmandu local). */
  from: string;
  to: string;
  storeId?: number | null;
}

export function useOrderSummary(p: DayParams) {
  return useQuery({
    queryKey: ['legacy', 'order-summary', p.from, p.to, p.storeId ?? null],
    queryFn: () =>
      get<OrderSummaryResponse>('/api/bi/reports/day-close/', {
        params: {
          from: p.from,
          to: p.to,
          ...(p.storeId != null ? { store_id: p.storeId } : {}),
        },
      }),
    enabled: Boolean(p.from && p.to),
    staleTime: 60_000,
  });
}

export function useProductSales(p: DayParams & { page?: number; page_size?: number; search?: string }) {
  return useQuery({
    queryKey: [
      'legacy',
      'product-sales',
      p.from,
      p.to,
      p.storeId ?? null,
      p.page ?? 1,
      p.page_size ?? 20,
      p.search ?? '',
    ],
    queryFn: () =>
      get<ProductSalesResponse>('/api/bi/reports/product-sales/', {
        params: {
          from: p.from,
          to: p.to,
          page: p.page ?? 1,
          page_size: p.page_size ?? 20,
          ...(p.storeId != null ? { store_id: p.storeId } : {}),
          ...(p.search ? { search: p.search } : {}),
        },
      }),
    enabled: Boolean(p.from && p.to),
    staleTime: 60_000,
  });
}
